import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, sendOtp, verifyOtp, resetOtpState } from '../../redux/AuthSlice/authSlice';
import { AlertCircle, BarChart3, CheckCircle, Eye, EyeOff, Handshake, Lock, Mail, Search, Clock, HelpCircle } from 'lucide-react';

import CareerChampWordmark from '../../components/Brand/CareerChampWordmark';
import api from '../../api/axios';
import LegalModal from '../../components/Modals/LegalModal';

const FEATURES = [
    { icon: <Search size={15} />, text: 'Discover companies about to hire — before jobs go public' },
    { icon: <Handshake size={15} />, text: 'Get insider referrals straight to the hiring manager' },
    { icon: <BarChart3 size={15} />, text: 'Track every conversation in one place' },
];

/* ── Shared left brand panel ── */
function BrandPanel() {
    return (
        <div
            className="hidden md:flex md:w-1/2 min-h-screen flex-col p-14"
            style={{ background: 'linear-gradient(155deg, #1A2454 0%, #3F57E0 55%, #6B7CF5 100%)' }}
        >
            {/* Logo — top-left */}
            <div className="flex items-center gap-3">
                <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif", fontWeight: 800, fontStyle: 'italic', color: 'white', fontSize: 17 }}>CC</span>
                </div>
                <CareerChampWordmark variant="light" className="text-[30px]" />
            </div>

            {/* Spacer — ~22% of viewport keeps headline in the left-lower area */}
            <div style={{ height: 'calc(100vh * 0.27)', flexShrink: 0 }} />

            {/* Headline + features — left-anchored */}
            <div className="mb-auto text-left">
                <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                    Skip the Resume<br />Black Hole
                </h1>
                <p className="text-blue-200 text-sm mb-8 leading-relaxed" style={{ maxWidth: 360 }}>
                    Get referred to hiring managers at companies about to hire — before the job is even posted.
                </p>
                <div className="space-y-4">
                    {FEATURES.map((f, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                                {f.icon}
                            </div>
                            <span className="text-blue-100 text-sm">{f.text}</span>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
}

/* ── 6-digit OTP boxes ── */
function OtpBoxes({ value, onChange }) {
    const refs = useRef([]);
    const digits = value.padEnd(6, ' ').split('').map(c => c === ' ' ? '' : c);

    const update = (idx, char) => {
        const next = [...digits];
        next[idx] = char;
        onChange(next.join('').trimEnd());
        if (char && idx < 5) refs.current[idx + 1]?.focus();
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === 'Backspace') {
            if (digits[idx]) { update(idx, ''); }
            else if (idx > 0) { refs.current[idx - 1]?.focus(); update(idx - 1, ''); }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        onChange(pasted);
        refs.current[Math.min(pasted.length, 5)]?.focus();
    };

    return (
        <div className="flex gap-3">
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={el => refs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => { const c = e.target.value.replace(/\D/g, '').slice(-1); update(i, c); }}
                    onKeyDown={e => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl outline-none transition-all"
                    style={{
                        border: d ? '2px solid #3F57E0' : '1px solid #E6E8EE',
                        background: d ? '#EEF1FE' : 'white',
                        color: d ? '#3F57E0' : '#94A3B8',
                        boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                />
            ))}
        </div>
    );
}

const Signup = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, emailSentTo, otpVerified, verifiedEmail } = useSelector(s => s.auth);

    // All user data collected upfront in step 1
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [legalModal, setLegalModal] = useState(null);
    const [linkedInToast, setLinkedInToast] = useState(false);

    const [otp, setOtp] = useState('');
    const [step, setStep] = useState(1); // 1=form, 2=otp
    const [otpTimer, setOtpTimer] = useState(0);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        dispatch(resetOtpState());
        return () => { dispatch(resetOtpState()); };
    }, [dispatch]);

    useEffect(() => {
        if (!isTimerActive || otpTimer <= 0) { setIsTimerActive(false); return; }
        const t = setInterval(() => setOtpTimer(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [otpTimer, isTimerActive]);

    // Capture a Refer & Earn code from the URL (?ref=) once, so it survives the
    // OTP step and is attached to the final signup.
    useEffect(() => {
        const ref = new URLSearchParams(window.location.search).get('ref');
        if (ref) localStorage.setItem('cc_referral', ref);
    }, []);

    // After OTP verified, immediately register
    useEffect(() => {
        if (otpVerified && verifiedEmail === email && step === 2) {
            const name = [firstName, lastName].filter(Boolean).join(' ');
            const ref = new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('cc_referral') || null;
            dispatch(register({ email, password, name, ref })).then(result => {
                if (ref) localStorage.removeItem('cc_referral');
                if (register.fulfilled.match(result)) {
                    navigate('/', { state: { showOnboardingModal: true } });
                } else {
                    setNotice({ type: 'error', message: result.payload || 'Could not create account. Please try again.' });
                }
            });
        }
    }, [otpVerified, verifiedEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    const showError = (msg) => setNotice({ type: 'error', message: msg || 'Something went wrong.' });

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        setNotice(null);
        if (!email || !email.includes('@')) { showError('Please enter a valid email address.'); return; }
        if (!password || password.length < 6) { showError('Password must be at least 6 characters.'); return; }
        if (!termsAccepted) { showError('Please accept the Terms of Service and Privacy Policy to continue.'); return; }

        const result = await dispatch(sendOtp(email));
        if (sendOtp.fulfilled.match(result)) {
            setStep(2);
            setOtpTimer(30);
            setIsTimerActive(true);
        } else {
            showError(result.payload);
        }
    };

    const handleVerifyOtp = async () => {
        setNotice(null);
        if (otp.length !== 6) { showError('Please enter all 6 digits.'); return; }
        setIsVerifying(true);
        const result = await dispatch(verifyOtp({ email, otp }));
        setIsVerifying(false);
        if (verifyOtp.rejected.match(result)) showError(result.payload);
        // registration is triggered by the useEffect above
    };

    const handleResendOtp = async () => {
        if (isTimerActive) return;
        const result = await dispatch(sendOtp(email));
        if (sendOtp.fulfilled.match(result)) {
            setOtpTimer(30);
            setIsTimerActive(true);
        } else {
            showError(result.payload);
        }
    };

    const handleGoogle = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${baseURL}/api/auth/google`, '_self');
    };

    const handleLinkedIn = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${baseURL}/api/auth/linkedin`, '_self');
    };

    return (
        <>
        <div className="h-screen overflow-y-auto flex flex-col md:flex-row">
            <BrandPanel />

            {/* ── Right panel ── */}
            <div className="w-full md:w-1/2 min-h-screen flex flex-col bg-gray-50">

                {/* Top-right link */}
                <div className="flex justify-end p-6">
                    <button type="button" onClick={() => navigate('/login')}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                        Already have an account?{' '}
                        <span className="font-semibold" style={{ color: '#3F57E0' }}>Sign in →</span>
                    </button>
                </div>

                <div className="flex-1 flex items-center justify-center px-8 pb-8">
                    <div className="w-full max-w-md text-left">

                        {/* ─ Step 1: registration form ─ */}
                        {step === 1 && (
                            <form onSubmit={handleSubmitForm} noValidate>
                                {/* 14-day free trial badge */}
                                <div className="mb-5">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border"
                                        style={{ color: '#3F57E0', borderColor: '#3F57E0', background: '#EEF1FE' }}>
                                        7-day free trial · no card required
                                    </span>
                                </div>

                                <div className="mb-6">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-1">Create your account</h2>
                                    <p className="text-gray-500 text-sm">Start finding better-matched jobs in 2 minutes.</p>
                                </div>

                                <div className="space-y-4">
                                    {/* Name row */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">First name</label>
                                            <input
                                                type="text"
                                                className="block w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                                placeholder="Anya"
                                                value={firstName}
                                                onChange={e => setFirstName(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Last name</label>
                                            <input
                                                type="text"
                                                className="block w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                                placeholder="Mehta"
                                                value={lastName}
                                                onChange={e => setLastName(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <Mail className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <input
                                                type="email"
                                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                                placeholder="anya@company.com"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <Lock className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                                                placeholder="At least 6 characters"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                required
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3.5 flex items-center">
                                                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Terms */}
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-gray-300"
                                            style={{ accentColor: '#3F57E0' }}
                                            checked={termsAccepted}
                                            onChange={e => setTermsAccepted(e.target.checked)} />
                                        <span className="text-xs text-gray-500 leading-relaxed">
                                            I agree to Career Champ's{' '}
                                            <button type="button" onClick={() => setLegalModal('terms')} className="hover:underline bg-transparent border-0 p-0 cursor-pointer font-medium" style={{ color: '#3F57E0' }}>Terms of Service</button>
                                            {' '}and{' '}
                                            <button type="button" onClick={() => setLegalModal('privacy')} className="hover:underline bg-transparent border-0 p-0 cursor-pointer font-medium" style={{ color: '#3F57E0' }}>Privacy Policy</button>
                                        </span>
                                    </label>

                                    {notice && <NoticeBanner notice={notice} navigate={navigate} />}

                                    <button type="submit" disabled={loading}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                        style={{ backgroundColor: '#3F57E0' }}>
                                        {loading ? 'Sending verification…' : 'Create account →'}
                                    </button>
                                </div>

                                {/* OR divider */}
                                <div className="relative my-5">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase tracking-wider">
                                        <span className="px-3 bg-gray-50 text-gray-400">or sign up with</span>
                                    </div>
                                </div>

                                {/* Social buttons — side by side */}
                                <div className="grid grid-cols-2 gap-3">
                                    <button type="button" onClick={handleGoogle}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm">
                                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="h-4 w-4" />
                                        Google
                                    </button>
                                    <div className="relative w-full">
                                        <button type="button"
                                            onClick={() => { setLinkedInToast(true); setTimeout(() => setLinkedInToast(false), 3000); }}
                                            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium text-white transition-opacity shadow-sm"
                                            style={{ backgroundColor: '#0A66C2', opacity: 0.7, cursor: 'default' }}>
                                            <svg className="h-4 w-4 fill-white flex-shrink-0" viewBox="0 0 24 24">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                            </svg>
                                            LinkedIn
                                        </button>
                                        {linkedInToast && (
                                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-9 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-dmsans text-white shadow-lg whitespace-nowrap"
                                                style={{ background: '#0F172A' }}>
                                                <span>🚧</span> Coming soon
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </form>
                        )}

                        {/* ─ Step 2: OTP verification ─ */}
                        {step === 2 && (
                            <div>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                                    style={{ background: '#EEF1FE' }}>
                                    <Mail className="h-5 w-5" style={{ color: '#3F57E0' }} />
                                </div>
                                <div className="mb-6">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-1">Check your email</h2>
                                    <p className="text-gray-500 text-sm">
                                        We sent a 6-digit code to{' '}
                                        <span className="font-semibold text-gray-800">{emailSentTo || email}</span>.
                                        It expires in 10 minutes.
                                    </p>
                                </div>

                                <OtpBoxes value={otp} onChange={setOtp} />

                                <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
                                    <Clock className="h-3 w-3 flex-shrink-0" />
                                    Didn't get it?{' '}
                                    <button type="button" onClick={handleResendOtp}
                                        disabled={isTimerActive || loading}
                                        className="font-semibold disabled:text-gray-400"
                                        style={{ color: isTimerActive ? undefined : '#3F57E0' }}>
                                        {isTimerActive ? `Resend in ${otpTimer}s` : 'Resend code'}
                                    </button>
                                </div>

                                {notice && <div className="mt-4"><NoticeBanner notice={notice} navigate={navigate} /></div>}

                                <button type="button" onClick={handleVerifyOtp}
                                    disabled={isVerifying || otp.length < 6 || loading}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-white mt-6 transition-opacity hover:opacity-90 disabled:opacity-50"
                                    style={{ backgroundColor: '#3F57E0' }}>
                                    {isVerifying || loading ? 'Verifying…' : 'Verify and continue →'}
                                </button>

                                <div className="mt-5 p-3.5 rounded-xl border flex gap-2.5 items-start text-xs text-gray-500 leading-relaxed"
                                    style={{ background: '#F6F7FB', borderColor: '#E6E8EE' }}>
                                    <HelpCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <span>
                                        Can't find it? Check your spam folder.
                                        We'll never ask for your code over chat or email.
                                    </span>
                                </div>

                                <div className="mt-4 text-center">
                                    <button type="button" onClick={() => { setStep(1); dispatch(resetOtpState()); setOtp(''); setNotice(null); }}
                                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                                        ← Back to form
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>

        <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
        </>
    );
};

function NoticeBanner({ notice, navigate }) {
    return (
        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
            notice.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-700'
        }`}>
            {notice.type === 'success'
                ? <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />}
            <div>
                <p className="font-semibold">{notice.type === 'success' ? 'Success' : 'Action needed'}</p>
                <p className="mt-0.5 leading-5">{notice.message}</p>
                {notice.message?.toLowerCase().includes('already registered') && navigate && (
                    <div className="mt-2 flex flex-wrap gap-3 text-sm font-medium">
                        <button type="button" onClick={() => navigate('/login')} className="text-red-800 hover:underline">Log in</button>
                        <button type="button" onClick={() => navigate('/forgotpass')} className="text-red-800 hover:underline">Forgot password</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Signup;
